import { Check, Clock, X } from 'lucide-react';
import type { VerificationStatus } from '@kulum/shared';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md';
}

export function Badge({ children, variant = 'default', size = 'sm' }: BadgeProps) {
  const variants = {
    default: 'bg-[#F0EDE8] text-[#777]',
    success: 'bg-[#EAF7EE] text-[#2D7D56]',
    warning: 'bg-[#FFF6E8] text-[#AF6F24]',
    error: 'bg-[#FDECEC] text-[#C0392B]',
    info: 'bg-[#EEF4FF] text-[#3D63B8]',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-[11px]',
    md: 'px-3 py-1 text-xs',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${variants[variant]} ${sizes[size]}`}
    >
      {children}
    </span>
  );
}

// Verification Badge Component
interface VerificationBadgeProps {
  label: string;
  status: VerificationStatus;
  size?: 'sm' | 'md';
}

export function VerificationBadge({ label, status, size = 'sm' }: VerificationBadgeProps) {
  const statusConfig = {
    none: {
      variant: 'default' as const,
      icon: null,
    },
    pending: {
      variant: 'warning' as const,
      icon: <Clock className="w-3 h-3" />,
    },
    approved: {
      variant: 'success' as const,
      icon: <Check className="w-3 h-3" />,
    },
    rejected: {
      variant: 'error' as const,
      icon: <X className="w-3 h-3" />,
    },
  };

  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} size={size}>
      {config.icon}
      {label}
    </Badge>
  );
}
